import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../../api';

 

// Async thunk to fetch videos data from API
export const fetchVideos = createAsyncThunk(
  'videos/fetchVideos',
  async ({page = 1, searchTerm=''}, { rejectWithValue }) => {
    try {
      const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/admin/get-started-videos-list?page=${page}`, {searchTerm});
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch videos');
    }
  }
);

// Async thunk to delete a video
export const deleteVideo = createAsyncThunk(
  'videos/deleteVideo',
  async (videoId, { dispatch, rejectWithValue }) => {
    try {
      await api.delete(`${process.env.REACT_APP_API_BASE_URL}/admin/get-started-videos/`+videoId, { id: videoId });
      dispatch(fetchVideos({ page: 1 })); // Refresh videos list after deletion
      return videoId;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to delete video');
    }
  }
);

// Async thunk to create a new video
export const createVideo = createAsyncThunk(
  'videos/createVideo',
  async (videoData, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/admin/get-started-videos`, videoData);
      dispatch(fetchVideos({ page: videoData.page || 1 })); // Refresh videos list after creation
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create video');
    }
  }
);

// Async thunk to update an existing video
export const updateVideo = createAsyncThunk(
  'videos/updateVideo',
  async ( videoData , { dispatch, rejectWithValue }) => {
    try {
      const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/admin/update-get-started-videos`, videoData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update video');
    }
  }
);

// Async thunk to fetch categories data from API
export const fetchCategories = createAsyncThunk(
  'categories/fetchCategories',
  async ({page = 1, searchTerm=''}, { rejectWithValue }) => {
    try {
      const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/admin/video-categories-list?page=${page}`, {searchTerm});
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch categories');
    }
  }
);

// Async thunk to delete a category
export const deleteCategory = createAsyncThunk(
  'categories/deleteCategory',
  async (categoryId, { dispatch, rejectWithValue }) => {
    try {
      await api.delete(`${process.env.REACT_APP_API_BASE_URL}/admin/video-categories/`+categoryId, { id: categoryId });
      dispatch(fetchCategories({ page: 1 })); // Refresh categories list after deletion
      return categoryId;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to delete category');
    }
  }
);

// Async thunk to create a new category
export const createCategory = createAsyncThunk(
  'categories/createCategory',
  async ({id='',categoryData}, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/admin/video-categories`, categoryData);
      dispatch(fetchCategories({ page: categoryData.page || 1 })); // Refresh categories list after creation
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create category');
    }
  }
);

// Async thunk to update an existing category
export const updateCategory = createAsyncThunk(
  'categories/updateCategory',
  async ({id,categoryData}, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/admin/update-video-categories`, categoryData);
      dispatch(fetchCategories({ page: categoryData.page || 1 })); // Refresh categories list after update
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update category');
    }
  }
);

const videoSlice = createSlice({
  name: 'admin_videos',
  initialState: {
    videos: [],
    categories: [],
    currentVideoPage: 1,
    currentCategoryPage: 1,
    totalVideoPages: 1,
    totalCategoryPages: 1,
    loading: false,
    error: null,
    successMessage: null,
  },
  reducers: {
    setVideoPage: (state, action) => {
      state.currentVideoPage = action.payload;
    },
    setCategoryPage: (state, action) => {
      state.currentCategoryPage = action.payload;
    },
    clearMessages: (state) => {
      state.error = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVideos.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVideos.fulfilled, (state, action) => {
        state.loading = false;
        state.videos = action.payload.data.data;
        state.currentVideoPage = action.payload.data.current_page || 1;
        state.totalVideoPages = action.payload.data.last_page || 0;
      })
      .addCase(fetchVideos.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(deleteVideo.fulfilled, (state, action) => {
      })
      .addCase(deleteVideo.rejected, (state, action) => {
      })
      .addCase(createVideo.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.successMessage = 'Video created successfully';
        } else {
          state.error = action.payload.message; // Handle error from response
        }
      })
      .addCase(createVideo.rejected, (state, action) => {
        state.error = action.payload.message; // Handle error on rejection
      })
      .addCase(updateVideo.fulfilled, (state, action) => {
        if (action.payload.success) {
          const index = state.videos.findIndex(video => video.id === action.payload.data.id);
          if (index !== -1) {
            state.videos[index] = action.payload.data; // Update with new data
          }
          state.successMessage = 'Video updated successfully';
        } else {
          state.error = action.payload.message; // Handle error from response
        }
      })
      .addCase(updateVideo.rejected, (state, action) => {
        state.error = action.payload.message; // Handle error on rejection
      })
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload.data.data;
        state.currentCategoryPage = action.payload.data.current_page || 1;
        state.totalCategoryPages = action.payload.data.last_page || 0;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteCategory.fulfilled, (state, action) => {
      })
      .addCase(deleteCategory.rejected, (state, action) => {
      })
      .addCase(createCategory.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.successMessage = 'Category created successfully';
        } else {
          state.error = action.payload.message; // Handle error from response
        }
      })
      .addCase(createCategory.rejected, (state, action) => {
        state.error = action.payload.message; // Handle error on rejection
      })
      .addCase(updateCategory.fulfilled, (state, action) => {
        if (action.payload.success) {
          const index = state.categories.findIndex(category => category.id === action.payload.data.id);
          if (index !== -1) {
            state.categories[index] = action.payload.data; // Update with new data
          }
          state.successMessage = 'Category updated successfully';
        } else {
          state.error = action.payload.message; // Handle error from response
        }
      })
      .addCase(updateCategory.rejected, (state, action) => {
        state.error = action.payload.message; // Handle error on rejection
      });
  }
});

export const { setVideoPage, setCategoryPage, clearMessages } = videoSlice.actions;

export default videoSlice.reducer;
